import { dashboardErrorClass } from './dashboard-error-kind';

// The dashboard render outcome in BI — SHARED CONTRACT with iOS and Android `DashboardContentBi`
// (keep the component names, the field keys and the enum values identical on all three surfaces).
//
// Two src:198 loadFinish (evid 5) events make "did this reader's dashboard render" countable from BI alone,
// for a monitor that can read nothing else:
//  - `dashboard-web` — the dashboard's render, carrying `outcome` (ready | empty | error; isSuccessful=false on
//    error), `dashboard_slug`, `page_slug` and `platform`. The phones add the widget inventory counts
//    (`widget_count`, `pending_count`); web cannot, because the document renders in an opaque-origin iframe the
//    host cannot read.
//  - `dashboard-widget-error` — one per widget the runtime reports as failed (isSuccessful=false), carrying
//    `widget_slug`, `dashboard_slug`, `page_slug`, `error_class` and `platform`.
// Ids, enums and counts only: the error message is read to classify it and is never sent.

export const DASHBOARD_RENDER_COMPONENT = 'dashboard-web';
export const DASHBOARD_WIDGET_ERROR_COMPONENT = 'dashboard-widget-error';
/** The entity type a dashboard is reported under — the documented lowercase-name fallback (no proto entity). */
export const DASHBOARD_ENTITY_TYPE = 'dashboard';
export const DASHBOARD_BI_PLATFORM = 'web';
/** The home dashboard's slug, as the harness names it (`HOME_DASHBOARD_SLUG` in lib/dashboard). */
const HOME_SLUG = 'catch-up';

export type DashboardRenderOutcome = 'ready' | 'empty' | 'error';

type Fields = Record<string, string>;

const dropEmpty = (fields: Record<string, string | undefined>): Fields => {
  const out: Fields = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value) out[key] = value;
  }
  return out;
};

/** Which dashboard is on screen: the dashboard selector (`catch-up` for the home) and the drill-in page. */
export function dashboardPageFields(dashboardSlug?: string, page?: string): Fields {
  return dropEmpty({ dashboard_slug: dashboardSlug || HOME_SLUG, page_slug: page });
}

/** `base` with the render `outcome` and the platform. */
export function dashboardOutcomeFields(base: Fields | undefined, outcome: DashboardRenderOutcome): Fields {
  return { ...base, outcome, platform: DASHBOARD_BI_PLATFORM };
}

/** The event's entity: the dashboard the fields name. */
export function dashboardEntity(fields: Fields | undefined): { id: string; type: string } {
  return { id: fields?.dashboard_slug || HOME_SLUG, type: DASHBOARD_ENTITY_TYPE };
}

/** Fields for one widget runtime error. `message` is read ONLY to classify it — it is never sent. */
export function widgetErrorFields(base: Fields | undefined, widgetSlug: string | undefined, message: string | undefined): Fields {
  return dropEmpty({
    dashboard_slug: base?.dashboard_slug || HOME_SLUG,
    page_slug: base?.page_slug,
    widget_slug: (widgetSlug ?? '').slice(0, 60),
    error_class: dashboardErrorClass(message ?? ''),
    platform: DASHBOARD_BI_PLATFORM,
  });
}
